import { useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Form, Modal, Spin } from "antd";
import { Check, X } from "lucide-react";
import { FC, useMemo } from "react";
import { useNotification } from "~/hooks";
import { useGetAllStaff } from "~/services/operation";
import {
  useGetVesselById,
  useUpdateVessel,
  VESSEL_KEYS,
} from "~/services/vessel";
import { VesselStaffRole } from "~/utils/constants";
import { useVesselStore } from "../store/vessel.store";

type Props = {
  open?: string;
  onClose: () => void;
};

interface StaffAssignment {
  staffId: string;
  roles: VesselStaffRole[];
  selected: boolean;
  staffName?: string; // Added for display purposes
}

interface FormValues {
  assignments: StaffAssignment[];
}

export const StaffAssignmentModal: FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const { success, error } = useNotification();
  const { data: vessel, isLoading } = useGetVesselById(open);
const { data: staffs, isLoading: loadingStaffs } = useGetAllStaff({ 
  getAll: true,
  pageSize: 100  // Get all staff for assignment
});
  const { mutate: updateVesselStaff, isPending: isUpdating } =
    useUpdateVessel();

  const qc = useQueryClient();

  const { filters } = useVesselStore();

  // Initialize form with current assignments and available staff
  const initialValues = useMemo(() => {
    if (!staffs?.items || !vessel) return { assignments: [] };

    return {
      assignments: staffs.items.map((staff) => {
        // Check if this staff is already assigned to the vessel
        const existingAssignment = vessel.vesselStaffs?.find(
          (vs) => vs.staff.id === staff.id
        );

        return {
          staffId: staff.id,
          staffName: staff.user?.username || staff.user?.firstName || `Staff ${staff.id.slice(-4)}`, // Store name for easier access
          roles: existingAssignment?.roles || [],
          selected: !!existingAssignment,
        };
      }),
    };
  }, [staffs?.items, vessel]);

  // Reset form when vessel data changes
  useMemo(() => {
    if (initialValues.assignments.length > 0) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  // Custom validator to ensure at least one driver and one lead
  const validateAssignments = (_: any, value: StaffAssignment[]) => {
    const selectedAssignments = value.filter((a) => a.selected);
    const hasDriver = selectedAssignments.some((a) =>
      a.roles?.includes(VesselStaffRole.DRIVER)
    );
    const hasLead = selectedAssignments.some((a) =>
      a.roles?.includes(VesselStaffRole.LEAD)
    );

    if (!hasDriver && !hasLead) {
      return Promise.reject("At least 1 Driver and 1 Lead required");
    }
    if (!hasDriver) {
      return Promise.reject("At least 1 Driver required");
    }
    if (!hasLead) {
      return Promise.reject("At least 1 Lead required");
    }

    return Promise.resolve();
  };

  const handleSubmit = (values: FormValues) => {
    if (!vessel?.id) return;

    const staffAssignments = values.assignments
      .filter((assignment) => assignment.selected)
      .map((assignment) => ({
        staffId: assignment.staffId,
        roles: assignment.roles || [],
      }));

    updateVesselStaff(
      {
        id: vessel.id,
        staffAssignments,
      },
      {
        onSuccess: () => {
          success({
            message: "Staff assigned successfully",
            description: "The staff have been assigned to the vessel.",
          });
          qc.invalidateQueries({
            queryKey: VESSEL_KEYS.GET_VESSELS(filters),
          });
          onClose();
        },
        onError: (err) => {
          error({
            message: "Failed to assign staff",
            description: err.message,
          });
        },
      }
    );
  };

  return (
    <Modal
      open={!!open}
      onCancel={onClose}
      footer={null}
      width={"70%"}
      title={
        <h3 className="text-lg font-medium">Assign Staff to {vessel?.name}</h3>
      }
      destroyOnHidden
    >
      <Spin spinning={isLoading || loadingStaffs}>
        <Form
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.List
            name="assignments"
            rules={[{ validator: validateAssignments }]}
          >
            {(fields) => (
              <div>
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Available Staff
                    </h4>
                    <div className="text-xs text-gray-500">
                      <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        D
                      </span>{" "}
                      = Driver,
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full ml-1">
                        L
                      </span>{" "}
                      = Lead
                    </div>
                  </div>

                  <div className="border rounded-lg divide-y">
                    {fields.map((field) => {
                      // Get the staff data for this field
                      const staffData = initialValues.assignments[field.name];
                      if (!staffData) return null;

                      return (
                        <div
                          key={field.key}
                          className="p-3 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex items-center">
                            <Form.Item
                              {...field}
                              name={[field.name, "selected"]}
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox className="h-4 w-4 text-blue-600 mr-3" />
                            </Form.Item>

                            <span>{staffData.staffName}</span>

                            {/* Hidden fields for staffId and staffName */}
                            <Form.Item
                              {...field}
                              name={[field.name, "staffId"]}
                              hidden
                            >
                              <input type="hidden" />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              name={[field.name, "staffName"]}
                              hidden
                            >
                              <input type="hidden" />
                            </Form.Item>
                          </div>

                          <div className="flex space-x-2">
                            {/* Combined role checkboxes */}
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => {
                                const selected = getFieldValue([
                                  "assignments",
                                  field.name,
                                  "selected",
                                ]);

                                return (
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "roles"]}
                                    noStyle
                                  >
                                    <Checkbox.Group className="flex items-center">
                                      {/* Driver checkbox - only if staff has driver capability */}
                                      {staffs?.items[field.name]?.driver && (
                                        <Checkbox
                                          value={VesselStaffRole.DRIVER}
                                          disabled={!selected}
                                        >
                                          <span className="text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded-full">
                                            Driver
                                          </span>
                                        </Checkbox>
                                      )}

                                      {/* Lead checkbox - only if staff has foreman capability */}
                                      {staffs?.items[field.name]?.lead && (
                                        <Checkbox
                                          value={VesselStaffRole.LEAD}
                                          disabled={!selected}
                                          className="ml-2"
                                        >
                                          <span className="text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full">
                                            Lead
                                          </span>
                                        </Checkbox>
                                      )}
                                    </Checkbox.Group>
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Currently Assigned Section */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Currently Assigned Staff
                    </h4>
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldsError, getFieldValue }) => {
                        const errors = getFieldsError().filter(
                          ({ errors }) => errors.length
                        );

                        // Check validation status for the message display
                        const assignments = getFieldValue("assignments") || [];
                        const selectedAssignments = assignments.filter(
                          (a: StaffAssignment) => a?.selected
                        );
                        const hasDriver = selectedAssignments.some(
                          (a: StaffAssignment) =>
                            a?.roles?.includes(VesselStaffRole.DRIVER)
                        );
                        const hasLead = selectedAssignments.some(
                          (a: StaffAssignment) =>
                            a?.roles?.includes(VesselStaffRole.LEAD)
                        );

                        const isValid = hasDriver && hasLead;

                        return (
                          <div
                            className={`text-xs flex items-center ${
                              !isValid ? "text-red-500" : "text-green-500"
                            }`}
                          >
                            {errors.length ? (
                              errors[0].errors[0]
                            ) : (
                              <>
                                <Check size={14} className="mr-1" /> Required: 1
                                Driver & 1 Lead
                              </>
                            )}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>

                  <div className="border rounded-lg p-3">
                    <div className="flex flex-wrap gap-2">
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const assignments =
                            getFieldValue("assignments") || [];
                          const selected = assignments.filter(
                            (a: StaffAssignment) => a?.selected
                          );

                          if (selected.length === 0) {
                            return (
                              <div className="w-full text-center text-gray-400">
                                No staff assigned
                              </div>
                            );
                          }

                          return selected.map(
                            (assignment: StaffAssignment, idx: number) => {
                              const fieldIndex = assignments.findIndex(
                                (a: StaffAssignment) =>
                                  a.staffId === assignment.staffId
                              );

                              return (
                                <div
                                  key={idx}
                                  className="bg-gray-100 px-3 py-1 rounded-full text-gray-700 text-sm flex items-center"
                                >
                                  <span className="font-medium">
                                    {assignment.staffName}
                                  </span>
                                  <div className="flex ml-2 space-x-1">
                                    {assignment.roles?.includes(
                                      VesselStaffRole.DRIVER
                                    ) && (
                                      <span className="bg-green-100 text-green-800 px-1 rounded-full text-xs">
                                        D
                                      </span>
                                    )}
                                    {assignment.roles?.includes(
                                      VesselStaffRole.LEAD
                                    ) && (
                                      <span className="bg-blue-100 text-blue-800 px-1 rounded-full text-xs">
                                        L
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    type="text"
                                    className="ml-1 flex items-center justify-center p-0 h-auto"
                                    icon={<X size={14} />}
                                    onClick={() => {
                                      if (fieldIndex >= 0) {
                                        form.setFieldValue(
                                          [
                                            "assignments",
                                            fieldIndex,
                                            "selected",
                                          ],
                                          false
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              );
                            }
                          );
                        }}
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Form.List>

          <div className="flex justify-end space-x-4 mt-6">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isUpdating}>
              Assign Staff
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
